import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Button } from './PageStyles';
import { activityTracker } from '../services/activityTracker';
import { notify } from '../utils/notifications';
import { buildApiUrl } from '../utils/apiClient';
import { useAuthStore } from '../stores/authStore';
import { FaCheckCircle, FaPlusCircle, FaMinusCircle, FaEquals, FaExchangeAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';
// ─── Styled Components ───────────────────────────────────────────────────────
const Container = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 32px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;
const Title = styled.h2 `
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 400;
  margin: 0 0 8px 0;
  letter-spacing: 0.5px;
`;
const Subtitle = styled.p `
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0 0 24px 0;
`;
const StepIndicator = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  padding: 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
`;
const Step = styled.div `
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${p => p.active ? 'var(--accent-gold, #996515)' : p.completed ? 'rgba(0, 200, 80, 0.8)' : 'var(--text-tertiary)'};
  font-weight: ${p => p.active ? '600' : '400'};

  &::after {
    content: '→';
    margin-left: 8px;
    color: var(--text-tertiary);
  }

  &:last-child::after {
    display: none;
  }
`;
const UploadSection = styled.div `
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;
const FileInput = styled.input `
  display: none;
`;
const FileLabel = styled.label `
  display: inline-block;
  padding: 12px 24px;
  background: transparent;
  border: 1px solid var(--border-color-hover);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 14px;
  letter-spacing: 0.5px;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--accent-color);
  }
`;
const FileName = styled.div `
  color: var(--text-secondary);
  font-size: 14px;
  margin-top: 8px;
`;
const ProgressSection = styled.div `
  margin: 24px 0;
  padding: 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
`;
const ProgressText = styled.div `
  color: var(--text-primary);
  font-size: 14px;
  margin-bottom: 8px;
`;
const ProgressBar = styled.div `
  width: 100%;
  height: 4px;
  background: var(--border-color);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
    animation: loading 1.5s infinite;
  }

  @keyframes loading {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;
// ─── Summary Cards ───────────────────────────────────────────────────────────
const SummaryGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;
const SummaryCard = styled.div `
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-left: 4px solid ${p => p.color};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const CardCount = styled.div `
  font-size: 28px;
  font-weight: 600;
  color: ${p => p.color};
`;
const CardLabel = styled.div `
  font-size: 13px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
// ─── Detail Tables ───────────────────────────────────────────────────────────
const CategorySection = styled.div `
  margin-bottom: 16px;
  border: 1px solid var(--border-color);
`;
const CategoryHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-tertiary);
  border-left: 4px solid ${p => p.color};
  cursor: pointer;
  user-select: none;

  &:hover {
    background: var(--bg-secondary);
  }
`;
const CategoryTitle = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;
const CategoryCount = styled.span `
  color: ${p => p.color};
  font-weight: 600;
`;
const TableWrapper = styled.div `
  max-height: 400px;
  overflow-y: auto;
  overflow-x: auto;
`;
const Table = styled.table `
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;
const Th = styled.th `
  text-align: left;
  padding: 8px 12px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-weight: 500;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
`;
const Td = styled.td `
  padding: 8px 12px;
  color: var(--text-primary);
  border-bottom: 1px solid rgba(255,255,255,0.03);
  white-space: nowrap;
`;
const DiffCell = styled.div `
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const OldValue = styled.span `
  color: rgba(255, 80, 80, 0.8);
  text-decoration: line-through;
  font-size: 12px;
`;
const NewValue = styled.span `
  color: rgba(80, 255, 120, 0.8);
  font-size: 12px;
`;
// ─── Import Options ──────────────────────────────────────────────────────────
const OptionsPanel = styled.div `
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  padding: 20px;
  margin-bottom: 24px;
`;
const OptionRow = styled.label `
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  cursor: pointer;
  color: var(--text-primary);
  font-size: 14px;
`;
const Checkbox = styled.input `
  accent-color: var(--accent-gold, #996515);
  width: 16px;
  height: 16px;
`;
const OptionDesc = styled.span `
  color: var(--text-secondary);
  font-size: 12px;
  margin-left: 26px;
  display: block;
`;
const ButtonRow = styled.div `
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;
const SuccessSection = styled.div `
  padding: 24px;
  background: rgba(0, 200, 80, 0.05);
  border: 1px solid rgba(0, 200, 80, 0.2);
  text-align: center;
`;
const SuccessTitle = styled.div `
  font-size: 20px;
  color: rgba(0, 200, 80, 0.9);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
const SuccessStats = styled.div `
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 16px;
  flex-wrap: wrap;
`;
const SuccessStat = styled.div `
  font-size: 14px;
  color: var(--text-secondary);
  strong {
    color: var(--text-primary);
  }
`;
// ─── Helper to format price ─────────────────────────────────────────────────
function fmt$(val) {
    if (!val)
        return '-';
    return `$${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
// ─── Component ───────────────────────────────────────────────────────────────
export const CSVValidator = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [csvText, setCsvText] = useState('');
    const [step, setStep] = useState('upload');
    const [validationResult, setValidationResult] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [expandedSections, setExpandedSections] = useState({ new: false, changed: true, removed: false, unchanged: false });
    // Import options
    const [importNew, setImportNew] = useState(true);
    const [updateChanged, setUpdateChanged] = useState(true);
    const [deactivateRemoved, setDeactivateRemoved] = useState(false);
    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };
    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file && file.name.toLowerCase().endsWith('.csv')) {
            setSelectedFile(file);
            setValidationResult(null);
            setImportResult(null);
            setStep('upload');
        }
        else {
            notify.warning('Invalid File', 'Please select a CSV file');
        }
    };
    const handleValidate = useCallback(async () => {
        if (!selectedFile) {
            notify.warning('No File', 'Please select a CSV file first');
            return;
        }
        setStep('validating');
        setValidationResult(null);
        try {
            const fileText = await selectedFile.text();
            setCsvText(fileText);
            const apiUrl = buildApiUrl('validate-csv');
            const { accessToken } = useAuthStore.getState();
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'text/csv',
                    ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
                },
                body: fileText
            });
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                throw new Error(`Server returned non-JSON response (${response.status}): ${text.substring(0, 200)}`);
            }
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || `Validation failed (${response.status})`);
            }
            setValidationResult(data);
            setStep('review');
            notify.success('Validation Complete', `${data.summary.csvTotal} diamonds analyzed`);
        }
        catch (error) {
            console.error('Validation error:', error);
            notify.error('Validation Failed', error instanceof Error ? error.message : 'Unknown error');
            setStep('upload');
        }
    }, [selectedFile]);
    const handleConfirmImport = useCallback(async () => {
        if (!csvText) {
            notify.error('Error', 'No CSV data available');
            return;
        }
        setStep('importing');
        try {
            const apiUrl = buildApiUrl('confirm-csv-import');
            const { accessToken } = useAuthStore.getState();
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
                },
                body: JSON.stringify({
                    csvData: csvText,
                    options: { importNew, updateChanged, deactivateRemoved }
                })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || `Import failed (${response.status})`);
            }
            setImportResult(data);
            setStep('complete');
            activityTracker.trackAdminAction('csv-validated-import', `Imported via validation pipeline`, {
                filename: selectedFile?.name,
                created: data.summary.created,
                updated: data.summary.updated,
                deactivated: data.summary.deactivated
            });
            notify.success('Import Complete', `${data.summary.created} created, ${data.summary.updated} updated`);
        }
        catch (error) {
            console.error('Import error:', error);
            notify.error('Import Failed', error instanceof Error ? error.message : 'Unknown error');
            setStep('review');
        }
    }, [csvText, importNew, updateChanged, deactivateRemoved, selectedFile]);
    const handleReset = () => {
        setSelectedFile(null);
        setCsvText('');
        setStep('upload');
        setValidationResult(null);
        setImportResult(null);
        // Reset the file input
        const input = document.getElementById('csv-validate-file');
        if (input)
            input.value = '';
    };
    const getStepState = (s) => {
        const order = ['upload', 'validating', 'review', 'importing', 'complete'];
        const current = order.indexOf(step);
        const target = order.indexOf(s);
        return { active: current === target, completed: current > target };
    };
    return (_jsxs(Container, { children: [_jsx(Title, { children: "CSV Import Pipeline" }), _jsx(Subtitle, { children: "Upload \u2192 Validate \u2192 Review Changes \u2192 Confirm Import" }), _jsxs(StepIndicator, { children: [_jsx(Step, { ...getStepState('upload'), children: "1. Upload CSV" }), _jsx(Step, { ...getStepState('validating'), children: "2. Validate" }), _jsx(Step, { ...getStepState('review'), children: "3. Review" }), _jsx(Step, { ...getStepState('importing'), children: "4. Import" }), _jsx(Step, { ...getStepState('complete'), children: "5. Done" })] }), (step === 'upload' || step === 'validating') && (_jsxs(UploadSection, { children: [_jsxs("div", { children: [_jsx(FileInput, { type: "file", id: "csv-validate-file", accept: ".csv", onChange: handleFileChange, disabled: step === 'validating' }), _jsx(FileLabel, { htmlFor: "csv-validate-file", children: selectedFile ? 'Change File' : 'Select CSV File' }), selectedFile && (_jsxs(FileName, { children: ["Selected: ", selectedFile.name] }))] }), _jsx(ButtonRow, { children: _jsx(Button, { onClick: handleValidate, disabled: !selectedFile || step === 'validating', children: step === 'validating' ? 'Validating...' : 'Validate Against Database' }) })] })), step === 'validating' && (_jsxs(ProgressSection, { children: [_jsx(ProgressText, { children: "Comparing CSV against Cosmos DB inventory..." }), _jsx(ProgressBar, {})] })), step === 'importing' && (_jsxs(ProgressSection, { children: [_jsx(ProgressText, { children: "Importing changes to Cosmos DB..." }), _jsx(ProgressBar, {})] })), step === 'review' && validationResult && (_jsxs(_Fragment, { children: [_jsxs(SummaryGrid, { children: [_jsxs(SummaryCard, { color: "rgba(80, 200, 120, 0.8)", children: [_jsx(CardCount, { color: "rgba(80, 200, 120, 0.8)", children: validationResult.summary.newCount }), _jsx(CardLabel, { children: "New (to add)" })] }), _jsxs(SummaryCard, { color: "rgba(255, 180, 50, 0.8)", children: [_jsx(CardCount, { color: "rgba(255, 180, 50, 0.8)", children: validationResult.summary.changedCount }), _jsx(CardLabel, { children: "Changed (to update)" })] }), _jsxs(SummaryCard, { color: "rgba(255, 80, 80, 0.8)", children: [_jsx(CardCount, { color: "rgba(255, 80, 80, 0.8)", children: validationResult.summary.removedCount }), _jsx(CardLabel, { children: "Removed (not in CSV)" })] }), _jsxs(SummaryCard, { color: "rgba(150, 150, 150, 0.6)", children: [_jsx(CardCount, { color: "rgba(150, 150, 150, 0.6)", children: validationResult.summary.unchangedCount }), _jsx(CardLabel, { children: "Unchanged" })] })] }), validationResult.newItems.length > 0 && (_jsxs(CategorySection, { children: [_jsxs(CategoryHeader, { color: "rgba(80, 200, 120, 0.8)", onClick: () => toggleSection('new'), children: [_jsxs(CategoryTitle, { children: [_jsx(FaPlusCircle, { color: "rgba(80, 200, 120, 0.8)" }), "New Diamonds", _jsxs(CategoryCount, { color: "rgba(80, 200, 120, 0.8)", children: ["(", validationResult.newItems.length, ")"] })] }), expandedSections.new ? _jsx(FaChevronUp, { size: 12 }) : _jsx(FaChevronDown, { size: 12 })] }), expandedSections.new && (_jsx(TableWrapper, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Stock #" }), _jsx(Th, { children: "Shape" }), _jsx(Th, { children: "Carat" }), _jsx(Th, { children: "Color" }), _jsx(Th, { children: "Clarity" }), _jsx(Th, { children: "Cut" }), _jsx(Th, { children: "Lab" }), _jsx(Th, { children: "Price" })] }) }), _jsx("tbody", { children: validationResult.newItems.map(item => (_jsxs("tr", { children: [_jsx(Td, { children: item.stockNumber }), _jsx(Td, { children: item.shape }), _jsx(Td, { children: item.caratWeight }), _jsx(Td, { children: item.color }), _jsx(Td, { children: item.clarity }), _jsx(Td, { children: item.cut }), _jsx(Td, { children: item.lab }), _jsx(Td, { children: fmt$(item.totalPrice) })] }, item.stockNumber))) })] }) }))] })), validationResult.changedItems.length > 0 && (_jsxs(CategorySection, { children: [_jsxs(CategoryHeader, { color: "rgba(255, 180, 50, 0.8)", onClick: () => toggleSection('changed'), children: [_jsxs(CategoryTitle, { children: [_jsx(FaExchangeAlt, { color: "rgba(255, 180, 50, 0.8)" }), "Changed Diamonds", _jsxs(CategoryCount, { color: "rgba(255, 180, 50, 0.8)", children: ["(", validationResult.changedItems.length, ")"] })] }), expandedSections.changed ? _jsx(FaChevronUp, { size: 12 }) : _jsx(FaChevronDown, { size: 12 })] }), expandedSections.changed && (_jsx(TableWrapper, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Stock #" }), _jsx(Th, { children: "Shape" }), _jsx(Th, { children: "Carat" }), _jsx(Th, { children: "Color" }), _jsx(Th, { children: "Clarity" }), _jsx(Th, { children: "Changed Fields" })] }) }), _jsx("tbody", { children: validationResult.changedItems.map(item => (_jsxs("tr", { children: [_jsx(Td, { children: item.stockNumber }), _jsx(Td, { children: item.csv.shape }), _jsx(Td, { children: item.csv.caratWeight }), _jsx(Td, { children: item.csv.color }), _jsx(Td, { children: item.csv.clarity }), _jsx(Td, { children: item.changes.map((c, i) => (_jsxs(DiffCell, { children: [_jsxs("span", { style: { color: 'var(--text-secondary)', fontSize: 11 }, children: [c.field, ":"] }), _jsx(OldValue, { children: typeof c.dbValue === 'number' ? (c.field.includes('rice') ? fmt$(c.dbValue) : c.dbValue) : c.dbValue || '(empty)' }), _jsx(NewValue, { children: typeof c.csvValue === 'number' ? (c.field.includes('rice') ? fmt$(c.csvValue) : c.csvValue) : c.csvValue || '(empty)' })] }, i))) })] }, item.stockNumber))) })] }) }))] })), validationResult.removedItems.length > 0 && (_jsxs(CategorySection, { children: [_jsxs(CategoryHeader, { color: "rgba(255, 80, 80, 0.8)", onClick: () => toggleSection('removed'), children: [_jsxs(CategoryTitle, { children: [_jsx(FaMinusCircle, { color: "rgba(255, 80, 80, 0.8)" }), "Removed from CSV (still in DB)", _jsxs(CategoryCount, { color: "rgba(255, 80, 80, 0.8)", children: ["(", validationResult.removedItems.length, ")"] })] }), expandedSections.removed ? _jsx(FaChevronUp, { size: 12 }) : _jsx(FaChevronDown, { size: 12 })] }), expandedSections.removed && (_jsx(TableWrapper, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Stock #" }), _jsx(Th, { children: "Shape" }), _jsx(Th, { children: "Carat" }), _jsx(Th, { children: "Color" }), _jsx(Th, { children: "Clarity" }), _jsx(Th, { children: "Lab" }), _jsx(Th, { children: "Status" }), _jsx(Th, { children: "Price" })] }) }), _jsx("tbody", { children: validationResult.removedItems.map(item => (_jsxs("tr", { children: [_jsx(Td, { children: item.stockNumber }), _jsx(Td, { children: item.shape }), _jsx(Td, { children: item.caratWeight }), _jsx(Td, { children: item.color }), _jsx(Td, { children: item.clarity }), _jsx(Td, { children: item.lab }), _jsx(Td, { children: item.status || '-' }), _jsx(Td, { children: fmt$(item.totalPrice) })] }, item.stockNumber))) })] }) }))] })), validationResult.unchangedItems.length > 0 && (_jsxs(CategorySection, { children: [_jsxs(CategoryHeader, { color: "rgba(150, 150, 150, 0.5)", onClick: () => toggleSection('unchanged'), children: [_jsxs(CategoryTitle, { children: [_jsx(FaEquals, { color: "rgba(150, 150, 150, 0.5)" }), "Unchanged", _jsxs(CategoryCount, { color: "rgba(150, 150, 150, 0.5)", children: ["(", validationResult.unchangedItems.length, ")"] })] }), expandedSections.unchanged ? _jsx(FaChevronUp, { size: 12 }) : _jsx(FaChevronDown, { size: 12 })] }), expandedSections.unchanged && (_jsx(TableWrapper, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Stock #" }), _jsx(Th, { children: "Shape" }), _jsx(Th, { children: "Carat" }), _jsx(Th, { children: "Color" }), _jsx(Th, { children: "Clarity" }), _jsx(Th, { children: "Lab" }), _jsx(Th, { children: "Price" })] }) }), _jsx("tbody", { children: validationResult.unchangedItems.map(item => (_jsxs("tr", { children: [_jsx(Td, { children: item.stockNumber }), _jsx(Td, { children: item.shape }), _jsx(Td, { children: item.caratWeight }), _jsx(Td, { children: item.color }), _jsx(Td, { children: item.clarity }), _jsx(Td, { children: item.lab }), _jsx(Td, { children: fmt$(item.totalPrice) })] }, item.stockNumber))) })] }) }))] })), _jsxs(OptionsPanel, { children: [_jsx(Title, { style: { fontSize: 16, marginBottom: 16 }, children: "Import Options" }), _jsxs(OptionRow, { children: [_jsx(Checkbox, { type: "checkbox", checked: importNew, onChange: e => setImportNew(e.target.checked) }), "Add ", validationResult.summary.newCount, " new diamonds"] }), _jsx(OptionDesc, { children: "Creates new diamond entries in the database" }), _jsxs(OptionRow, { children: [_jsx(Checkbox, { type: "checkbox", checked: updateChanged, onChange: e => setUpdateChanged(e.target.checked) }), "Update ", validationResult.summary.changedCount, " changed diamonds"] }), _jsx(OptionDesc, { children: "Overwrites existing entries with new CSV data" }), _jsxs(OptionRow, { children: [_jsx(Checkbox, { type: "checkbox", checked: deactivateRemoved, onChange: e => setDeactivateRemoved(e.target.checked) }), "Deactivate ", validationResult.summary.removedCount, " removed diamonds"] }), _jsx(OptionDesc, { children: "Sets status to \"deactivated\" for diamonds not in the CSV (careful!)" })] }), _jsxs(ButtonRow, { children: [_jsx(Button, { onClick: handleConfirmImport, disabled: !importNew && !updateChanged && !deactivateRemoved, children: "Confirm Import" }), _jsx(Button, { variant: "secondary", onClick: handleReset, children: "Cancel" })] })] })), step === 'complete' && importResult && (_jsxs(SuccessSection, { children: [_jsxs(SuccessTitle, { children: [_jsx(FaCheckCircle, {}), "Import Successful"] }), _jsxs(SuccessStats, { children: [_jsxs(SuccessStat, { children: [_jsx("strong", { children: importResult.summary.created }), " created"] }), _jsxs(SuccessStat, { children: [_jsx("strong", { children: importResult.summary.updated }), " updated"] }), _jsxs(SuccessStat, { children: [_jsx("strong", { children: importResult.summary.deactivated }), " deactivated"] }), importResult.summary.errorCount > 0 && (_jsxs(SuccessStat, { style: { color: 'rgba(255,80,80,0.8)' }, children: [_jsx("strong", { children: importResult.summary.errorCount }), " errors"] }))] }), importResult.errors.length > 0 && (_jsxs("div", { style: { marginTop: 16, textAlign: 'left' }, children: [_jsx("div", { style: { color: 'rgba(255,80,80,0.8)', fontSize: 13, marginBottom: 8 }, children: "Errors:" }), importResult.errors.slice(0, 10).map((e, i) => (_jsxs("div", { style: { fontSize: 12, color: 'var(--text-secondary)', padding: '4px 0' }, children: [e.stockNumber, " (", e.action, "): ", e.error] }, i)))] })), _jsx(ButtonRow, { style: { justifyContent: 'center', marginTop: 24 }, children: _jsx(Button, { variant: "secondary", onClick: handleReset, children: "Import Another File" }) })] }))] }));
};
